/* =========================================================================
   ClueVine — share text + links (pure)
   Builds the text a player shares and the link inside it. The link is a
   challenge link (see engine/links.ts): it reopens the exact same puzzle, and
   carries the sender's score so a friend can try to beat it.
   ========================================================================= */

import { SITE_URL } from '../config';
import { challengeHash, type PuzzleRef } from '../engine/links';
import { commas } from './format';

/** The site origin shared results link back to (canonical SITE_URL, else this origin). */
export function siteUrl(): string {
  if (SITE_URL) return (SITE_URL as string).replace(/\/+$/, '');
  return typeof window !== 'undefined' ? window.location.origin : 'https://cluevine.com';
}

export function siteDomain(): string {
  return siteUrl().replace(/^https?:\/\//, '') || 'cluevine.com';
}

export function challengeUrl(ref: PuzzleRef, beat?: number, by?: string, base: string = siteUrl()): string {
  return `${base}/#${challengeHash({ ref, beat, by })}`;
}

export interface ShareTextInput {
  modeName: string;
  /** "#270", "#265 (archive)", or "" for endless. */
  tag: string;
  difficultyLabel?: string;
  grid: readonly string[];
  headline: string;
  score: number;
  url: string;
}

export function buildShareText(v: ShareTextInput): string {
  const head = [`ClueVine · ${v.modeName}${v.tag ? ` ${v.tag}` : ''}`, v.difficultyLabel].filter(Boolean).join(' · ');
  const line = [v.headline, `${commas(v.score)} pts`].filter(Boolean).join(' · ');
  return [head, v.grid.join('\n'), line, v.url].filter(Boolean).join('\n');
}

export type GardenPlot = 'won' | 'lost' | 'open';

const PLOT_EMOJI: Record<GardenPlot, string> = { won: '🌸', lost: '🥀', open: '🌱' };

/** One message for the whole day: an emoji per mode, the tally, the streak, and a link. */
export function buildGardenText(opts: {
  day: number;
  plots: readonly GardenPlot[];
  streak: number;
  url: string;
}): string {
  const won = opts.plots.filter((p) => p === 'won').length;
  const played = opts.plots.filter((p) => p !== 'open').length;
  const tally = `${won}/${opts.plots.length} bloomed${played > won ? ` · ${played - won} wilted` : ''}`;
  const streak = opts.streak > 0 ? ` · 🔥${opts.streak}` : '';
  return [
    `ClueVine · Daily #${opts.day} garden`,
    opts.plots.map((p) => PLOT_EMOJI[p]).join(''),
    `${tally}${streak}`,
    opts.url,
  ].join('\n');
}
